import type {
  RequestDetail,
  RequestOperationSnapshot,
  RequestStatus,
  RequestSummary,
} from "../api/requests";
import {
  isOperationComplete,
  isOperationFailed,
  toStatusPresentation,
  type StatusPresentation,
  type StatusTone,
} from "./operationStatus";
import { requestToStatusPresentation } from "./requestStatus";

const TERMINAL_REQUEST_STATUSES = new Set<RequestStatus>([
  "funded",
  "failed",
  "expired",
  "cancelled",
]);

export interface DisplayStatusFlags extends StatusPresentation {
  statusKey: string;
  isComplete: boolean;
  isFailed: boolean;
}

const toDisplayFlags = (presentation: StatusPresentation): DisplayStatusFlags => ({
  ...presentation,
  statusKey: presentation.labelKey,
  isComplete: presentation.tone === "completed",
  isFailed:
    presentation.tone === "failed" || presentation.tone === "expired",
});

/** Resolve the live operation status from a summary or detail payload. */
export const resolveOperationStatus = (
  item: Pick<RequestSummary, "operation"> & {
    operation?: RequestOperationSnapshot | RequestDetail["operation"] | null;
  }
): string | undefined => item.operation?.status;

/** Label + tone for list/receipt rows. */
export const getDisplayStatus = (
  requestStatus: RequestStatus,
  operationStatus?: string
): DisplayStatusFlags => {
  const hasLiveOperation =
    Boolean(operationStatus) &&
    (requestStatus === "accepted" || requestStatus === "pending") &&
    !TERMINAL_REQUEST_STATUSES.has(requestStatus);

  if (hasLiveOperation && operationStatus) {
    return toDisplayFlags(toStatusPresentation(operationStatus));
  }

  return toDisplayFlags(requestToStatusPresentation(requestStatus));
};

/** i18n key for the receipt hero line. */
export const requestReceiptHeroKey = (detail: RequestDetail): string => {
  const operationStatus = detail.operation?.status;
  const hasOperation = detail.operation != null;

  if (detail.status === "funded" || isOperationComplete(operationStatus)) {
    return "receipt.youReceived";
  }
  if (isOperationFailed(operationStatus) || detail.status === "failed") {
    return "receipt.transferFailed";
  }
  if (detail.status === "cancelled") {
    return "receipt.statusCancelled";
  }
  if (detail.status === "expired") {
    return "receipt.statusExpired";
  }
  if (hasOperation) {
    return "receipt.youReceived";
  }
  return "receipt.youRequested";
};

/** Status row on the receipt when an operation is linked. */
export const receiptStatusDisplay = (
  detail: RequestDetail
): DisplayStatusFlags => {
  const operationStatus = detail.operation?.status;
  if (
    detail.operation &&
    (detail.status === "accepted" || detail.status === "pending")
  ) {
    return getDisplayStatus(detail.status, operationStatus);
  }
  return getDisplayStatus(detail.status, operationStatus);
};

export type { StatusTone, StatusPresentation };
